import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, FileText, ChevronRight } from 'lucide-react';
import axios from 'axios';
import InvoiceView from '../components/InvoiceView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyOrdersPage = () => {
  const [searchParams] = useSearchParams();
  const [phone, setPhone] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    const phoneParam = searchParams.get('phone');
    if (phoneParam && phoneParam.length === 10) {
      setPhone(phoneParam);
      fetchOrders(phoneParam);
    }
  }, [searchParams]);

  const fetchOrders = async (phoneNum) => {
    setLoading(true);
    setSearched(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/orders/customer/${phoneNum}`);
      setOrders(res.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (value) => {
    const numericOnly = value.replace(/\D/g, '').slice(0, 10);
    setPhone(numericOnly);
    if (numericOnly.length > 0 && numericOnly.length < 10) {
      setPhoneError('Enter 10-digit phone number');
    } else {
      setPhoneError('');
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) {
      setPhoneError('Phone number must be 10 digits');
      return;
    }
    fetchOrders(phone);
  };

  const statusColor = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    delivered: 'bg-green-100 text-green-800',
  };

  return (
    <div className="min-h-screen bg-[#F6F5F2]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#1A3626] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="my-orders-title">
          My Orders
        </h1>
        <p className="text-gray-600 mb-8">Enter your phone number to view your orders and invoices.</p>

        <form onSubmit={handleSearch} className="bg-white rounded-lg p-6 shadow-sm mb-6" data-testid="order-search-form">
          <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
          <div className="flex gap-3">
            <div className="flex-1">
              <input
                type="tel"
                maxLength={10}
                value={phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder="Enter 10-digit phone number"
                className={`w-full bg-white border-2 ${phoneError ? 'border-red-400' : 'border-gray-300'} focus:border-[#1A3626] focus:ring-0 rounded-md p-3 text-base`}
                data-testid="order-phone-input"
              />
              {phoneError && <p className="text-red-500 text-sm mt-1">{phoneError}</p>}
            </div>
            <button
              type="submit"
              disabled={loading}
              className="bg-[#C05A3E] text-white hover:bg-[#A64B31] rounded-md px-6 py-3 font-semibold transition-colors flex items-center gap-2 disabled:opacity-50"
              data-testid="search-orders-btn"
            >
              <Search className="h-4 w-4" />
              Search
            </button>
          </div>
        </form>

        {loading && (
          <div className="text-center py-8 text-gray-500">Loading orders...</div>
        )}

        {searched && !loading && orders.length === 0 && (
          <div className="bg-white rounded-lg p-8 shadow-sm text-center" data-testid="no-orders">
            <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-lg text-gray-600">No orders found for this phone number.</p>
          </div>
        )}

        {orders.length > 0 && (
          <div className="space-y-3" data-testid="orders-list">
            <h2 className="text-lg font-semibold text-[#1A3626]">Your Orders ({orders.length})</h2>
            {orders.map((order) => (
              <div
                key={order.id}
                className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-gray-100"
                onClick={() => setSelectedOrder(order)}
                data-testid={`customer-order-${order.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-semibold text-[#1A3626] text-sm">{order.invoice_number || 'N/A'}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusColor[order.status] || 'bg-gray-100 text-gray-800'}`}>
                        {(order.status || 'pending').toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {order.items?.length} item{order.items?.length !== 1 ? 's' : ''} | {order.order_date_ist || 'N/A'} at {order.order_time_ist || 'N/A'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {order.items?.map(i => i.product_name).join(', ')}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedOrder && (
          <InvoiceView
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            isAdmin={false}
          />
        )}
      </div>
    </div>
  );
};

export default MyOrdersPage;
